/**
 * Multi-Channel Inbox Component
 * Unified inbox for messages from WhatsApp, Telegram, Email, SMS, Live Chat, etc.
 */
import { useState, useEffect, useRef } from 'react';
import { 
  Inbox,
  MessageCircle,
  Mail,
  Phone,
  Send,
  Search,
  Filter,
  MoreVertical,
  Check,
  CheckCheck,
  Clock,
  Star,
  StarOff,
  Archive,
  Trash2,
  RefreshCw,
  User,
  Bot,
  X,
  ChevronLeft,
  Loader2,
  Facebook,
  Globe,
  Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Channel icons
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const TelegramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// Channel configuration
const CHANNELS = {
  whatsapp: { name: 'WhatsApp', icon: WhatsAppIcon, color: 'bg-green-500', textColor: 'text-green-600' },
  telegram: { name: 'Telegram', icon: TelegramIcon, color: 'bg-blue-500', textColor: 'text-blue-600' },
  email: { name: 'Email', icon: Mail, color: 'bg-gray-500', textColor: 'text-gray-600' },
  sms: { name: 'SMS', icon: Phone, color: 'bg-emerald-500', textColor: 'text-emerald-600' },
  live_chat: { name: 'Live Chat', icon: MessageCircle, color: 'bg-purple-500', textColor: 'text-purple-600' },
  facebook: { name: 'Facebook', icon: Facebook, color: 'bg-blue-600', textColor: 'text-blue-700' },
  instagram: { name: 'Instagram', icon: InstagramIcon, color: 'bg-pink-500', textColor: 'text-pink-600' },
  website: { name: 'Website', icon: Globe, color: 'bg-indigo-500', textColor: 'text-indigo-600' },
};

export default function MultiChannelInbox({ isOpen, onClose }) {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterChannel, setFilterChannel] = useState('all');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [aiAssistEnabled, setAiAssistEnabled] = useState(true);
  const messagesEndRef = useRef(null);

  // Fetch conversations
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/inbox/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      // Load demo data
      setConversations(getDemoConversations());
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await axios.get(`${API}/api/inbox/messages/${conversationId}`);
      setMessages(response.data.messages || []);
    } catch (error) {
      // Demo messages
      setMessages(getDemoMessages(conversationId));
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSending(true);
    const tempMessage = {
      id: Date.now(),
      content: newMessage,
      sender: 'agent',
      timestamp: new Date().toISOString(),
      status: 'sending'
    };
    
    setMessages(prev => [...prev, tempMessage]);
    setNewMessage('');
    
    try {
      // Send via API
      await axios.post(`${API}/api/inbox/send`, {
        conversation_id: selectedConversation.id,
        content: newMessage,
        channel: selectedConversation.channel
      });
      
      // Update message status
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? { ...m, status: 'sent' } : m
      ));
      
      toast.success('Message sent!');
    } catch (error) {
      // Demo mode - just mark as sent
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? { ...m, status: 'sent' } : m
      ));
    } finally {
      setSending(false);
    }
  };

  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    fetchMessages(conv.id);
  };

  // Filter conversations
  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.contact_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         conv.last_message?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesChannel = filterChannel === 'all' || conv.channel === filterChannel;
    return matchesSearch && matchesChannel;
  });

  // Demo data
  const getDemoConversations = () => [
    { id: '1', contact_name: 'John Smith', contact_avatar: null, channel: 'whatsapp', last_message: 'I need a quote for kitchen renovation', timestamp: new Date(Date.now() - 300000).toISOString(), unread: 2, starred: true },
    { id: '2', contact_name: 'Maria Garcia', contact_avatar: null, channel: 'email', last_message: 'Thank you for the estimate!', timestamp: new Date(Date.now() - 3600000).toISOString(), unread: 0, starred: false },
    { id: '3', contact_name: 'Robert Johnson', contact_avatar: null, channel: 'sms', last_message: 'When can you start the project?', timestamp: new Date(Date.now() - 7200000).toISOString(), unread: 1, starred: false },
    { id: '4', contact_name: 'Sarah Williams', contact_avatar: null, channel: 'live_chat', last_message: 'Is there a warranty on the work?', timestamp: new Date(Date.now() - 86400000).toISOString(), unread: 0, starred: true },
    { id: '5', contact_name: 'Michael Brown', contact_avatar: null, channel: 'telegram', last_message: 'Please send the contract', timestamp: new Date(Date.now() - 172800000).toISOString(), unread: 0, starred: false },
    { id: '6', contact_name: 'Emily Davis', contact_avatar: null, channel: 'facebook', last_message: 'Great work on the bathroom!', timestamp: new Date(Date.now() - 259200000).toISOString(), unread: 0, starred: false },
  ];

  const getDemoMessages = (convId) => {
    const conv = conversations.find(c => c.id === convId);
    return [
      { id: 1, content: `Hi, I'm interested in your construction services.`, sender: 'customer', timestamp: new Date(Date.now() - 7200000).toISOString() },
      { id: 2, content: `Hello! Thank you for reaching out. How can I help you today?`, sender: 'agent', timestamp: new Date(Date.now() - 7000000).toISOString(), status: 'read' },
      { id: 3, content: conv?.last_message || 'I need a quote for my project', sender: 'customer', timestamp: new Date(Date.now() - 300000).toISOString() },
    ];
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diff < 604800000) return date.toLocaleDateString([], { weekday: 'short' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-5xl m-auto rounded-2xl shadow-2xl overflow-hidden flex h-[80vh] animate-in zoom-in-95">
        {/* Sidebar - Conversation List */}
        <div className={`w-full md:w-80 border-r border-gray-200 flex flex-col ${selectedConversation && 'hidden md:flex'}`}>
          {/* Header */}
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Inbox className="w-6 h-6" />
                <h2 className="text-lg font-bold">Multi-Channel Inbox</h2>
              </div>
              <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/60 text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>

          {/* Channel Filters */}
          <div className="p-2 border-b border-gray-100 flex gap-1 overflow-x-auto">
            <button
              onClick={() => setFilterChannel('all')}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition ${
                filterChannel === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {Object.entries(CHANNELS).map(([key, channel]) => (
              <button
                key={key}
                onClick={() => setFilterChannel(key)}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition flex items-center gap-1 ${
                  filterChannel === key ? `${channel.color} text-white` : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <channel.icon className="w-3 h-3" />
                {channel.name}
              </button>
            ))}
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Inbox className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No conversations found</p>
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const channel = CHANNELS[conv.channel] || CHANNELS.live_chat;
                const ChannelIcon = channel.icon;
                
                return (
                  <button
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full p-3 flex items-start gap-3 hover:bg-gray-50 border-b border-gray-100 transition text-left ${
                      selectedConversation?.id === conv.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold">
                        {conv.contact_name?.charAt(0) || '?'}
                      </div>
                      <div className={`absolute -bottom-1 -right-1 w-5 h-5 ${channel.color} rounded-full flex items-center justify-center`}>
                        <ChannelIcon className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900 truncate">{conv.contact_name}</span>
                        <span className="text-xs text-gray-500">{formatTime(conv.timestamp)}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{conv.last_message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs ${channel.textColor}`}>{channel.name}</span>
                        {conv.starred && <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />}
                      </div>
                    </div>
                    
                    {/* Unread Badge */}
                    {conv.unread > 0 && (
                      <span className="bg-indigo-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                        {conv.unread}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!selectedConversation && 'hidden md:flex'}`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setSelectedConversation(null)}
                    className="md:hidden p-1 hover:bg-gray-200 rounded-lg"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white font-bold">
                    {selectedConversation.contact_name?.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{selectedConversation.contact_name}</h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      {(() => {
                        const channel = CHANNELS[selectedConversation.channel];
                        const Icon = channel?.icon || MessageCircle;
                        return (
                          <>
                            <Icon className="w-3 h-3" />
                            {channel?.name || 'Chat'}
                          </>
                        );
                      })()}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAiAssistEnabled(!aiAssistEnabled)}
                    className={`p-2 rounded-lg transition ${aiAssistEnabled ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-500'}`}
                    title={aiAssistEnabled ? 'AI Assist ON' : 'AI Assist OFF'}
                  >
                    <Sparkles className="w-4 h-4" />
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded-lg">
                    <Star className="w-4 h-4 text-gray-500" />
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded-lg">
                    <MoreVertical className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'agent' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[70%] ${
                      msg.sender === 'agent'
                        ? 'bg-indigo-500 text-white rounded-2xl rounded-br-sm'
                        : 'bg-white text-gray-900 rounded-2xl rounded-bl-sm shadow-sm'
                    } px-4 py-2`}>
                      <p className="text-sm">{msg.content}</p>
                      <div className={`flex items-center gap-1 mt-1 ${
                        msg.sender === 'agent' ? 'justify-end' : ''
                      }`}>
                        <span className={`text-xs ${msg.sender === 'agent' ? 'text-indigo-200' : 'text-gray-400'}`}>
                          {formatTime(msg.timestamp)}
                        </span>
                        {msg.sender === 'agent' && (
                          msg.status === 'read' ? <CheckCheck className="w-3 h-3 text-indigo-200" /> :
                          msg.status === 'sent' ? <Check className="w-3 h-3 text-indigo-200" /> :
                          <Clock className="w-3 h-3 text-indigo-200" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* AI Suggestion */}
              {aiAssistEnabled && messages.length > 0 && messages[messages.length - 1]?.sender === 'customer' && (
                <div className="px-4 py-2 bg-indigo-50 border-t border-indigo-100">
                  <div className="flex items-center gap-2 text-sm">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    <span className="text-indigo-700">AI Suggestion:</span>
                    <button
                      onClick={() => setNewMessage("Thank you for your inquiry! I'd be happy to help. Let me get you a quote right away.")}
                      className="text-indigo-600 hover:text-indigo-800 underline"
                    >
                      Use suggested reply
                    </button>
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t border-gray-200 bg-white">
                <div className="flex items-center gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || sending}
                    className="bg-indigo-500 hover:bg-indigo-600"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Inbox className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <h3 className="text-lg font-medium text-gray-700">Select a conversation</h3>
                <p className="text-sm">Choose from your multi-channel inbox</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
